import { forwardRef, useId } from 'react';
import { CircleAlert } from 'lucide-react';
import { cn } from './cn';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Field label rendered above the input. */
  label?: React.ReactNode;
  /** Error message (string) or error flag (boolean). Renders the danger (red) validation treatment. */
  error?: string | boolean;
  /** Helper text below the input (hidden when there is an error). */
  helperText?: React.ReactNode;
  /** Content rendered inside the field, before the input (replaces MUI startAdornment). */
  startSlot?: React.ReactNode;
  /** Content rendered inside the field, after the input (replaces MUI endAdornment). */
  endSlot?: React.ReactNode;
  inputSize?: InputSize;
  /** Stretch to fill the container width. Default true (matches MUI fullWidth usage). */
  fullWidth?: boolean;
  /** Class applied to the outer wrapper (label + field + helper). */
  containerClassName?: string;
}

const fieldSizes: Record<InputSize, string> = {
  sm: 'h-9 text-sm',
  md: 'h-11 text-sm',
  lg: 'h-12 text-base',
};

/**
 * Text input — replaces MUI <TextField /> (single-line). For multi-line use <Textarea />.
 * Wraps label + field + helper/error. Forwards ref to the <input> for Formik/refs.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      startSlot,
      endSlot,
      inputSize = 'md',
      fullWidth = true,
      required,
      disabled,
      className,
      containerClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const hasError = Boolean(error);
    const errorText = typeof error === 'string' ? error : undefined;

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full', containerClassName)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-content">
            {label}
            {required && <span className="ml-0.5 text-primary">*</span>}
          </label>
        )}

        <div
          className={cn(
            'group flex items-center gap-2 rounded-xl border bg-surface px-3.5 transition-all duration-200',
            // Field-validation errors use the danger (red) token + icon — the ONE place we
            // leave brand orange (see CONVENTIONS §5). Persistent ring so it reads as
            // "needs attention" even when unfocused.
            hasError
              ? 'border-danger ring-2 ring-danger/25 focus-within:border-danger focus-within:ring-danger/40'
              : 'border-line hover:border-primary/40 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30',
            disabled && 'opacity-60 pointer-events-none',
            fieldSizes[inputSize],
          )}
        >
          {startSlot && <span className="shrink-0 text-muted">{startSlot}</span>}
          <input
            ref={ref}
            id={inputId}
            required={required}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-content placeholder:text-muted/70 outline-none border-0',
              // Hide native number spinners everywhere (BidDialog price, filters, forms…).
              '[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [appearance:textfield]',
              className,
            )}
            {...props}
          />
          {endSlot ? (
            <span className="shrink-0 text-muted">{endSlot}</span>
          ) : (
            hasError && (
              <span className="shrink-0 text-danger" aria-hidden="true">
                <CircleAlert size={18} strokeWidth={2} />
              </span>
            )
          )}
        </div>

        {errorText ? (
          <p className="flex items-start gap-1.5 text-xs font-medium text-danger">
            <CircleAlert size={13} strokeWidth={2.5} className="mt-px shrink-0" aria-hidden="true" />
            <span>{errorText}</span>
          </p>
        ) : helperText ? (
          <p className="text-xs text-muted">{helperText}</p>
        ) : null}
      </div>
    );
  },
);

Input.displayName = 'Input';
